// Los datos de la agenda, portados del `<script>` del mockup `portal-cobrador-agenda.html`. No hay
// endpoint de agenda todavía: cuando exista, esto se reemplaza por su adaptador y el sello de mock
// de la pantalla se apaga solo.
//
// El recorte es el del mock: créditos asignados a C-01 y (en mora o con compromiso activo).
import type { CollectorAgendaRow } from './types';

/** El día contra el que el mock calcula toda la agenda. Es dato del fixture, no el reloj. */
export const AGENDA_TODAY = '2026-07-06';

/** El cobrador logueado en el Portal. Con backend saldrá de la sesión. */
export const AGENDA_COLLECTOR = { id: 'C-01', name: 'Roberto Sánchez', zone: 'Zona 1' };

/** Los cobradores por id, para poner nombre al autor de cada contacto. */
export const COLLECTOR_NAMES: Record<string, string> = {
    'C-01': 'Roberto Sánchez',
    'C-02': 'María Aguilar',
    'C-03': 'Jorge Ramírez',
};

/** Lo que ofrece el combo «Tienda»: el padrón completo, como el mock. */
export const AGENDA_STORE_OPTIONS: readonly { id: string; label: string }[] = [
    { id: 'ST-01', label: 'Centro CDMX · Zona 1' },
    { id: 'ST-02', label: 'Iztapalapa · Zona 1' },
    { id: 'ST-03', label: 'Gustavo A. Madero · Zona 1' },
    { id: 'ST-04', label: 'Nezahualcóyotl · Zona 2' },
    { id: 'ST-05', label: 'Ecatepec · Zona 2' },
    { id: 'ST-06', label: 'Celular Express · Indep.' },
    { id: 'ST-07', label: 'Móvil Norte · Indep.' },
];

export const AGENDA_ROWS: readonly CollectorAgendaRow[] = [
    {
        creditId: 'CR-0003',
        customerId: 'CL-0003',
        customerName: 'Carlos Ramírez Torres',
        storeId: 'ST-02',
        storeName: 'Iztapalapa',
        productLabel: 'Motorola Moto G14',
        status: 'in_arrears',
        originatedOn: '2026-06-03',
        weeklyPaymentWithIvaCents: 25_520,
        overdueDays: 12,
        accumulatedArrears: 2,
        overdueInstallments: 2,
        overdueAmountCents: 44_000,
        device: { imei: '351234567890111', state: 'locked', lockedSince: '2026-06-15' },
        recentAttempts: 0,
        contacts: [
            {
                at: '2026-07-05T14:30',
                channel: 'call',
                outcome: 'promise',
                note: 'Cliente promete pagar $220 en tienda el 08-jul',
                collectorId: 'C-01',
            },
            {
                at: '2026-07-03T10:15',
                channel: 'call',
                outcome: 'no_answer',
                note: '',
                collectorId: 'C-01',
            },
            {
                at: '2026-07-01T16:45',
                channel: 'sms',
                outcome: 'sent',
                note: 'Recordatorio automático día 14 (M4 §6.3)',
                collectorId: 'system',
            },
            {
                at: '2026-06-28T09:00',
                channel: 'call',
                outcome: 'spoke',
                note: 'Cliente reporta pérdida de empleo. Voluntad de pago.',
                collectorId: 'C-01',
            },
            {
                at: '2026-06-26T11:20',
                channel: 'voice_blaster',
                outcome: 'voicemail',
                note: 'Blaster día 1 tras bloqueo',
                collectorId: 'system',
            },
        ],
        promises: [
            {
                day: '2026-07-08',
                amountCents: 22_000,
                channel: 'store',
                state: 'active',
                registeredOn: '2026-07-05',
                collectorId: 'C-01',
            },
            {
                day: '2026-06-25',
                amountCents: 22_000,
                channel: 'oxxo',
                state: 'broken',
                registeredOn: '2026-06-22',
                collectorId: 'C-01',
            },
            {
                day: '2026-06-10',
                amountCents: 22_000,
                channel: 'store',
                state: 'fulfilled',
                registeredOn: '2026-06-08',
                collectorId: 'C-01',
            },
        ],
        payments: [
            {
                installmentNumber: 2,
                amountCents: 21_585,
                paidOn: '2026-06-16',
                channel: 'oxxo',
            },
            {
                installmentNumber: 1,
                amountCents: 21_658,
                paidOn: '2026-06-09',
                channel: 'store',
            },
        ],
    },
    {
        creditId: 'CR-0008',
        customerId: 'CL-0008',
        customerName: 'Laura Jiménez Nava',
        storeId: 'ST-01',
        storeName: 'Centro CDMX',
        productLabel: 'Xiaomi Redmi Note 13',
        status: 'in_arrears',
        originatedOn: '2026-06-04',
        weeklyPaymentWithIvaCents: 44_080,
        overdueDays: 22,
        accumulatedArrears: 1,
        overdueInstallments: 4,
        overdueAmountCents: 152_000,
        device: { imei: '351234567890222', state: 'locked', lockedSince: '2026-06-25' },
        recentAttempts: 0,
        contacts: [
            {
                at: '2026-07-05T09:30',
                channel: 'call',
                outcome: 'no_answer',
                note: '',
                collectorId: 'C-01',
            },
            {
                at: '2026-07-02T14:15',
                channel: 'call',
                outcome: 'no_answer',
                note: '',
                collectorId: 'C-01',
            },
            {
                at: '2026-06-30T18:00',
                channel: 'sms',
                outcome: 'sent',
                note: 'Aviso reporte a Círculo en 5 días',
                collectorId: 'system',
            },
            {
                at: '2026-06-28T10:00',
                channel: 'call',
                outcome: 'wrong_number',
                note: 'Número no corresponde al titular. Solicitar actualización.',
                collectorId: 'C-01',
            },
        ],
        promises: [
            {
                day: '2026-06-30',
                amountCents: 38_000,
                channel: 'app',
                state: 'broken',
                registeredOn: '2026-06-28',
                collectorId: 'C-01',
            },
        ],
        payments: [
            {
                installmentNumber: 3,
                amountCents: 41_306,
                paidOn: '2026-06-24',
                channel: 'pcf_manual',
            },
            {
                installmentNumber: 2,
                amountCents: 41_448,
                paidOn: '2026-06-17',
                channel: 'oxxo',
            },
            {
                installmentNumber: 1,
                amountCents: 41_585,
                paidOn: '2026-06-10',
                channel: 'store',
            },
        ],
    },
];
